import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Button, Drawer, Modal, message } from "antd";
import {
  getSelectedCompanyProfiles,
  selectedCompanyHasProfile,
} from "../../utils/activityProfiles";
import {
  ArrowDownOutlined,
  AppstoreOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CoffeeOutlined,
  DashboardOutlined,
  DropboxOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  RocketOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  CartesianGrid,
  LineChart,
  Line as RechartsLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./HomePage.css";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { getProductionTerminology } from "../../GestionProductions/productionTerminology";
import MobileContextSelectors from "../../mobile/MobileContextSelectors";
import CommercialOnboardingAssistant from "../../components/onboarding/CommercialOnboardingAssistant";
import useIsMobile from "../../hooks/useIsMobile";
import RestaurantOwnerCockpit from "./RestaurantOwnerCockpit";
import { getNotificationDetailRows } from "../../utils/notificationDetailDisplay";
import DailyRecapDetail, {
  DailyRecapModalTitle,
} from "../../components/DailyRecapDetail";
import {
  ACTIVITY_ORIGIN_OPTIONS,
  getActivityActorOptionLabel,
  getActivitySourceLabel,
} from "./homeActivityPresentation";
import {
  buildHomeInterfaceShortcuts,
  filterHomeShortcuts,
  mergeHomeShortcutSelection,
  normalizeHomeShortcutPreference,
} from "./homeShortcutPreferences";

const COMMERCIAL_ONBOARDING_STORAGE_PREFIX = "elsa:commercial-onboarding";
const EMPTY_HOME_SHORTCUT_MENU_TREE = Object.freeze([]);

const buildCommercialOnboardingStorageKey = (companyId, suffix) =>
  `${COMMERCIAL_ONBOARDING_STORAGE_PREFIX}:${companyId || "unknown"}:${suffix}`;

const getGreeting = (hour) => {
  if (hour < 12) return "Bonjour";
  if (hour < 18) return "Bon apres-midi";
  return "Bonsoir";
};

const formatXof = (value) =>
  new Intl.NumberFormat("fr-FR").format(Math.round(value));

const normalizeSearchText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const createEmptyKpiToday = () => ({
  ca: 0,
  delta: "0%",
  deltaValue: 0,
  tickets: 0,
  panierMoyen: 0,
  cashShare: "0%",
  hourly: Array.from({ length: 24 }, () => 0),
});

const normalizeKpiToday = (payload = {}) => {
  const hourly = Array.isArray(payload.hourly)
    ? payload.hourly.slice(0, 24).map(toFiniteNumber)
    : [];

  while (hourly.length < 24) {
    hourly.push(0);
  }

  return {
    ca: toFiniteNumber(payload.ca),
    delta: typeof payload.delta === "string" ? payload.delta : "0%",
    deltaValue: toFiniteNumber(payload.deltaValue),
    tickets: toFiniteNumber(payload.tickets),
    panierMoyen: toFiniteNumber(payload.panierMoyen),
    cashShare: typeof payload.cashShare === "string" ? payload.cashShare : "0%",
    hourly,
  };
};

const createEmptyTeamActivity = () => ({
  connectedCount: 0,
  onlineUsers: [],
  feed: [],
  modules: [],
  actors: [],
  pagination: {
    hasMore: false,
    nextCursor: null,
  },
});

const normalizeTeamActivity = (payload = {}) => ({
  connectedCount: toFiniteNumber(payload.connectedCount),
  onlineUsers: Array.isArray(payload.onlineUsers) ? payload.onlineUsers : [],
  feed: Array.isArray(payload.feed) ? payload.feed : [],
  modules: Array.isArray(payload.modules)
    ? payload.modules.filter((module) => module?.key && module?.label)
    : [],
  actors: Array.isArray(payload.actors)
    ? payload.actors.filter((actor) => Number(actor?.id) > 0 && actor?.name)
    : [],
  pagination: {
    hasMore: Boolean(payload.pagination?.hasMore),
    nextCursor:
      typeof payload.pagination?.nextCursor === "string"
        ? payload.pagination.nextCursor
        : null,
  },
});

const normalizeAgendaPayload = (payload = {}) => {
  const items = Array.isArray(payload.items)
    ? payload.items
        .filter((item) => item && Number(item.id) > 0 && item.title)
        .map((item) => ({
          ...item,
          id: Number(item.id),
          data:
            item.data && typeof item.data === "object" && !Array.isArray(item.data)
              ? item.data
              : {},
          action:
            item.action &&
            typeof item.action.url === "string" &&
            item.action.url.startsWith("/")
              ? item.action
              : null,
        }))
    : [];

  return {
    items,
    total: Math.max(Number(payload.total) || 0, items.length),
    hasMore: Boolean(payload.has_more),
  };
};

const normalizeCommandSearchGroups = (payload = {}) => {
  if (!Array.isArray(payload.groups)) return [];

  return payload.groups
    .map((group) => ({
      key: group.key || "results",
      label: group.label || "Resultats",
      items: Array.isArray(group.items)
        ? group.items
            .filter((item) => item && item.id && item.title && item.path)
            .map((item) => ({
              id: String(item.id),
              type: item.type || "result",
              title: String(item.title),
              meta: item.meta ? String(item.meta) : "Resultat metier",
              amount: item.amount ? String(item.amount) : "",
              path: item.path,
            }))
        : [],
    }))
    .filter((group) => group.items.length > 0);
};

const getCommandIconForType = (type) => {
  switch (type) {
    case "product":
      return <AppstoreOutlined />;
    case "customer":
    case "supplier":
      return <TeamOutlined />;
    case "purchase":
      return <ShoppingCartOutlined />;
    case "sale":
      return <ShoppingOutlined />;
    default:
      return <SearchOutlined />;
  }
};

const formatPulseHourTick = (value) => {
  const hour = Number(value);
  if (!Number.isFinite(hour)) return "";
  if (hour >= 23) return "23h59";
  return `${String(Math.max(0, Math.floor(hour))).padStart(2, "0")}h`;
};

const formatYAxisTick = (value) => {
  const numericValue = toFiniteNumber(value);
  if (numericValue >= 1_000_000) return `${(numericValue / 1_000_000).toFixed(1)}M`;
  if (numericValue >= 1_000) return `${Math.round(numericValue / 1_000)}k`;
  return String(Math.round(numericValue));
};

const chartTooltipFormatter = (value, name) => [`${formatXof(value)} F`, name];

const chartTooltipLabelFormatter = (value) => {
  const hour = Number(value);
  if (!Number.isFinite(hour)) return "";
  const start = `${String(Math.max(0, Math.floor(hour))).padStart(2, "0")}h00`;
  const end = hour >= 23 ? "23h59" : `${String(Math.floor(hour) + 1).padStart(2, "0")}h00`;
  return `${start} - ${end}`;
};

const formatRelativeTime = (value, referenceDate) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "a l'instant";

  const diffMs = Math.max(0, referenceDate.getTime() - date.getTime());
  const diffMinutes = Math.floor(diffMs / 60_000);

  if (diffMinutes < 1) return "a l'instant";
  if (diffMinutes < 60) return `${diffMinutes} min`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} h`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} j`;
};

const formatActivityAmount = (feed) => {
  const amount = toFiniteNumber(feed.amount);
  if (amount > 0) return `${formatXof(amount)} F`;

  const quantity = toFiniteNumber(feed.quantity ?? feed.totalItems);
  return quantity > 0
    ? `${formatXof(quantity)} unité${quantity > 1 ? "s" : ""}`
    : "";
};

const getActivityIcon = (categoryKey) => {
  const icons = {
    sales: <ShoppingOutlined />,
    purchases: <ShoppingCartOutlined />,
    products: <AppstoreOutlined />,
    stock: <DropboxOutlined />,
    treasury: <WalletOutlined />,
    expenses: <FileTextOutlined />,
    production: <ExperimentOutlined />,
    ecommerce: <RocketOutlined />,
    hr: <TeamOutlined />,
    logistics: <DropboxOutlined />,
    hotel: <ShopOutlined />,
    administration: <SettingOutlined />,
  };

  return icons[categoryKey] || <ClockCircleOutlined />;
};

const OperationalHomePage = () => {
  const { user, hasPermission, refreshUserStatus } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const outletContext = useOutletContext();
  const homeShortcutMenuTree = Array.isArray(
    outletContext?.homeShortcutMenuTree,
  )
    ? outletContext.homeShortcutMenuTree
    : EMPTY_HOME_SHORTCUT_MENU_TREE;
  const navigate = useNavigate();
  const commandInputRef = useRef(null);
  const commandPaletteRef = useRef(null);
  const selectedCompanyRef = useRef(selectedCompany);
  selectedCompanyRef.current = selectedCompany;
  const productionTerminology = getProductionTerminology(
    getSelectedCompanyProfiles(user, selectedCompany)
  );
  const [now, setNow] = useState(() => new Date());
  const [kpiToday, setKpiToday] = useState(createEmptyKpiToday);
  const [kpiStatus, setKpiStatus] = useState("idle");
  const [teamActivity, setTeamActivity] = useState(createEmptyTeamActivity);
  const [teamStatus, setTeamStatus] = useState("idle");
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  const [activityHistory, setActivityHistory] = useState(
    createEmptyTeamActivity,
  );
  const [activityHistoryStatus, setActivityHistoryStatus] = useState("idle");
  const [activityLoadingMore, setActivityLoadingMore] = useState(false);
  const [activityFilters, setActivityFilters] = useState({
    module: "all",
    actorScope: "all",
    userId: "",
    warehouseId: selectedWarehouse ? String(selectedWarehouse) : "",
    startDate: "",
    endDate: "",
  });
  const [agendaItems, setAgendaItems] = useState([]);
  const [agendaTotal, setAgendaTotal] = useState(0);
  const [agendaStatus, setAgendaStatus] = useState("idle");
  const [selectedAgendaItem, setSelectedAgendaItem] = useState(null);
  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [agendaUpdatingId, setAgendaUpdatingId] = useState(null);
  const [commandQuery, setCommandQuery] = useState("");
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandActiveIndex, setCommandActiveIndex] = useState(0);
  const [commandSearchGroups, setCommandSearchGroups] = useState([]);
  const [commandSearchStatus, setCommandSearchStatus] = useState("idle");
  const [shortcutPreferenceKeys, setShortcutPreferenceKeys] = useState(null);
  const [shortcutPreferenceStatus, setShortcutPreferenceStatus] =
    useState("idle");
  const [shortcutCustomizerOpen, setShortcutCustomizerOpen] = useState(false);
  const [shortcutDraftKeys, setShortcutDraftKeys] = useState([]);
  const [shortcutSearchQuery, setShortcutSearchQuery] = useState("");
  const [shortcutSaving, setShortcutSaving] = useState(false);
  const [commercialOnboardingOpen, setCommercialOnboardingOpen] =
    useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const loadShortcutPreference = useCallback(
    async ({ signal } = {}) => {
      if (!selectedCompany) {
        setShortcutPreferenceKeys(null);
        setShortcutPreferenceStatus("idle");
        return { loaded: true, keys: null };
      }

      setShortcutPreferenceStatus("loading");
      const requestedCompanyId = selectedCompany;

      try {
        const { data } = await apiClient.get("/api/home-shortcuts", {
          params: { company_id: selectedCompany },
          signal,
          skipGlobalLoading: true,
        });
        if (
          signal?.aborted ||
          String(selectedCompanyRef.current) !== String(requestedCompanyId)
        ) {
          return { loaded: false, keys: null };
        }

        const keys = normalizeHomeShortcutPreference(data?.preference);
        setShortcutPreferenceKeys(keys);
        setShortcutPreferenceStatus("ready");
        return { loaded: true, keys };
      } catch (error) {
        if (signal?.aborted || error?.code === "ERR_CANCELED") {
          return { loaded: false, keys: null };
        }

        if (
          String(selectedCompanyRef.current) === String(requestedCompanyId)
        ) {
          setShortcutPreferenceKeys(null);
          setShortcutPreferenceStatus("error");
        }
        return { loaded: false, keys: null };
      }
    },
    [selectedCompany],
  );

  useEffect(() => {
    setShortcutCustomizerOpen(false);
    setShortcutDraftKeys([]);
    setShortcutSearchQuery("");
    setShortcutPreferenceKeys(null);

    if (!selectedCompany) {
      setShortcutPreferenceStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    void loadShortcutPreference({ signal: controller.signal });
    return () => controller.abort();
  }, [loadShortcutPreference, selectedCompany, user?.id]);

  const assignedWarehouses = useMemo(() => {
    return user && Array.isArray(user.assigned_warehouses)
      ? user.assigned_warehouses
      : [];
  }, [user]);

  const availableCompanies = useMemo(() => {
    const companyMap = new Map();

    assignedWarehouses.forEach((warehouse) => {
      if (warehouse.company_id && !companyMap.has(warehouse.company_id)) {
        companyMap.set(warehouse.company_id, {
          id: warehouse.company_id,
          name: warehouse.company_name || `Entreprise #${warehouse.company_id}`,
        });
      }
    });

    return Array.from(companyMap.values());
  }, [assignedWarehouses]);

  const warehousesForSelectedCompany = useMemo(() => {
    if (!selectedCompany) return [];

    return assignedWarehouses.filter(
      (warehouse) => warehouse.company_id === selectedCompany
    );
  }, [assignedWarehouses, selectedCompany]);

  const currentCompany = useMemo(() => {
    return (
      availableCompanies.find((company) => company.id === selectedCompany) ||
      (user?.company?.name
        ? { id: selectedCompany, name: user.company.name }
        : null)
    );
  }, [availableCompanies, selectedCompany, user?.company?.name]);

  const currentWarehouse = useMemo(() => {
    return (
      warehousesForSelectedCompany.find(
        (warehouse) => warehouse.id === selectedWarehouse
      ) || null
    );
  }, [selectedWarehouse, warehousesForSelectedCompany]);

  const firstName = useMemo(() => {
    const source =
      user?.first_name || user?.prenom || user?.name || user?.username || "Equipe";

    return String(source).trim().split(/\s+/)[0];
  }, [user]);

  const dateLabel = useMemo(() => {
    return new Intl.DateTimeFormat("fr-FR", {
      weekday: "short",
      day: "numeric",
      month: "long",
    })
      .format(now)
      .replace(".", "");
  }, [now]);

  const timeLabel = useMemo(() => {
    return new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(now);
  }, [now]);

  const commandKey = useMemo(() => {
    if (typeof navigator === "undefined") return "Ctrl K";
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? "Cmd K" : "Ctrl K";
  }, []);

  const workspaceReady = Boolean(selectedCompany && selectedWarehouse);
  const canViewAgenda = Boolean(
    user?.is_superadmin || hasPermission?.("Notifications.view")
  );
  const companyName = currentCompany?.name || "Entreprise a choisir";
  const warehouseName =
    currentWarehouse?.name ||
    (selectedWarehouse ? `Établissement #${selectedWarehouse}` : "Établissement a choisir");

  const loadAgenda = useCallback(
    async ({ signal, silent = false } = {}) => {
      if (!selectedCompany || !canViewAgenda) {
        setAgendaItems([]);
        setAgendaTotal(0);
        setAgendaStatus(canViewAgenda ? "idle" : "forbidden");
        return;
      }

      if (!silent) setAgendaStatus("loading");

      try {
        const { data } = await apiClient.get("/api/agenda/today", {
          params: {
            company_id: selectedCompany,
            ...(selectedWarehouse ? { warehouse_id: selectedWarehouse } : {}),
            limit: 5,
          },
          signal,
          skipGlobalLoading: silent,
        });
        const normalized = normalizeAgendaPayload(data);
        setAgendaItems(normalized.items);
        setAgendaTotal(normalized.total);
        setAgendaStatus("ready");
      } catch (error) {
        if (signal?.aborted || error?.code === "ERR_CANCELED") return;
        if (!silent) {
          setAgendaItems([]);
          setAgendaTotal(0);
          setAgendaStatus("error");
        }
      }
    },
    [canViewAgenda, selectedCompany, selectedWarehouse]
  );

  useEffect(() => {
    if (!selectedCompany || !canViewAgenda) {
      setAgendaItems([]);
      setAgendaTotal(0);
      setAgendaStatus(canViewAgenda ? "idle" : "forbidden");
      return undefined;
    }

    const controller = new AbortController();
    loadAgenda({ signal: controller.signal });
    const refreshTimer = window.setInterval(
      () => loadAgenda({ silent: true }),
      60_000
    );

    return () => {
      controller.abort();
      window.clearInterval(refreshTimer);
    };
  }, [canViewAgenda, loadAgenda, selectedCompany]);

  useEffect(() => {
    setAgendaModalOpen(false);
    setSelectedAgendaItem(null);
  }, [selectedCompany, selectedWarehouse]);

  useEffect(() => {
    if (!selectedCompany) {
      setKpiToday(createEmptyKpiToday());
      setKpiStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    setKpiStatus("loading");

    apiClient
      .get("/api/dashboard/today-sales-pulse", {
        params: {
          companyId: selectedCompany,
          ...(selectedWarehouse ? { warehouseId: selectedWarehouse } : {}),
        },
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setKpiToday(normalizeKpiToday(data));
        setKpiStatus("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
        setKpiToday(createEmptyKpiToday());
        setKpiStatus("error");
      });

    return () => controller.abort();
  }, [selectedCompany, selectedWarehouse]);

  const loadTeamActivity = useCallback(
    async ({ signal, silent = false } = {}) => {
      if (!selectedCompany) {
        setTeamActivity(createEmptyTeamActivity());
        setTeamStatus("idle");
        return;
      }

      if (!silent) setTeamStatus("loading");

      try {
        const { data } = await apiClient.get("/api/dashboard/team-activity", {
          params: {
            companyId: selectedCompany,
            ...(selectedWarehouse ? { warehouseId: selectedWarehouse } : {}),
            actorScope: "team",
            limit: 5,
          },
          signal,
          skipGlobalLoading: silent,
        });
        if (signal?.aborted) return;
        setTeamActivity(normalizeTeamActivity(data));
        setTeamStatus("ready");
      } catch (error) {
        if (signal?.aborted || error?.code === "ERR_CANCELED") return;
        if (!silent) {
          setTeamActivity(createEmptyTeamActivity());
          setTeamStatus("error");
        }
      }
    },
    [selectedCompany, selectedWarehouse],
  );

  useEffect(() => {
    if (!selectedCompany) {
      setTeamActivity(createEmptyTeamActivity());
      setTeamStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    loadTeamActivity({ signal: controller.signal });
    const refreshTimer = window.setInterval(
      () => loadTeamActivity({ signal: controller.signal, silent: true }),
      60_000,
    );

    return () => {
      controller.abort();
      window.clearInterval(refreshTimer);
    };
  }, [loadTeamActivity, selectedCompany]);

  const loadActivityHistory = useCallback(
    async ({ signal, append = false, cursor = null } = {}) => {
      if (!selectedCompany) {
        setActivityHistory(createEmptyTeamActivity());
        setActivityHistoryStatus("idle");
        return;
      }

      if (append) {
        setActivityLoadingMore(true);
      } else {
        setActivityLoadingMore(false);
        setActivityHistoryStatus("loading");
        setActivityHistory((current) => ({
          ...current,
          feed: [],
          pagination: {
            hasMore: false,
            nextCursor: null,
          },
        }));
      }

      try {
        const { data } = await apiClient.get("/api/dashboard/team-activity", {
          params: {
            companyId: selectedCompany,
            ...(activityFilters.warehouseId
              ? { warehouseId: activityFilters.warehouseId }
              : {}),
            limit: 20,
            includeFacets: true,
            ...(activityFilters.module !== "all"
              ? { module: activityFilters.module }
              : {}),
            ...(activityFilters.actorScope !== "all"
              ? { actorScope: activityFilters.actorScope }
              : {}),
            ...(activityFilters.userId
              ? { userId: activityFilters.userId }
              : {}),
            ...(activityFilters.startDate
              ? { startDate: activityFilters.startDate }
              : {}),
            ...(activityFilters.endDate
              ? { endDate: activityFilters.endDate }
              : {}),
            ...(cursor ? { cursor } : {}),
          },
          signal,
        });
        if (signal?.aborted) return;

        const normalized = normalizeTeamActivity(data);
        setActivityHistory((current) =>
          append
            ? {
                ...normalized,
                feed: [...current.feed, ...normalized.feed],
              }
            : normalized,
        );
        setActivityHistoryStatus("ready");
      } catch (error) {
        if (signal?.aborted || error?.code === "ERR_CANCELED") return;
        if (!append) {
          setActivityHistory(createEmptyTeamActivity());
          setActivityHistoryStatus("error");
        } else {
          message.error(
            "Impossible de charger davantage d’activités pour le moment.",
          );
        }
      } finally {
        if (!signal?.aborted) setActivityLoadingMore(false);
      }
    },
    [activityFilters, selectedCompany],
  );

  useEffect(() => {
    if (!activityDrawerOpen) return undefined;

    const controller = new AbortController();
    loadActivityHistory({ signal: controller.signal });
    return () => controller.abort();
  }, [activityDrawerOpen, loadActivityHistory]);

  useEffect(() => {
    setActivityDrawerOpen(false);
    setActivityHistory(createEmptyTeamActivity());
    setActivityFilters({
      module: "all",
      actorScope: "all",
      userId: "",
      warehouseId: selectedWarehouse ? String(selectedWarehouse) : "",
      startDate: "",
      endDate: "",
    });
  }, [selectedCompany, selectedWarehouse]);

  useEffect(() => {
    const normalizedQuery = normalizeSearchText(commandQuery);

    if (!commandOpen || !selectedCompany || normalizedQuery.length < 2) {
      setCommandSearchGroups([]);
      setCommandSearchStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    setCommandSearchStatus("loading");

    const timeoutId = window.setTimeout(() => {
      apiClient
        .get("/api/dashboard/global-search", {
          params: {
            companyId: selectedCompany,
            ...(selectedWarehouse ? { warehouseId: selectedWarehouse } : {}),
            q: commandQuery.trim(),
            limit: 5,
          },
          signal: controller.signal,
        })
        .then(({ data }) => {
          if (controller.signal.aborted) return;
          setCommandSearchGroups(normalizeCommandSearchGroups(data));
          setCommandSearchStatus("ready");
        })
        .catch((error) => {
          if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
          setCommandSearchGroups([]);
          setCommandSearchStatus("error");
        });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [commandOpen, commandQuery, selectedCompany, selectedWarehouse]);

  const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const hasPermissionPrefix = (prefix) =>
    Boolean(user?.is_superadmin || user?.role === "SysAdmin") ||
    userPermissions.some((permission) => permission.startsWith(prefix));

  const canViewDashboard = hasPermission?.("Gestion Commerciale.Dashboard.view");
  const canUsePos = hasPermission?.("Gestion Commerciale.POS.use");
  const canViewClients = hasPermission?.(
    "Gestion Commerciale.Entites.Clients.view"
  );
  const canViewSuppliers = hasPermission?.(
    "Gestion Commerciale.Entites.Fournisseurs.view"
  );
  const canViewEntities = canViewClients || canViewSuppliers;
  const entitiesLandingPath = canViewClients
    ? "/gestion-entites/clients"
    : "/gestion-entites/fournisseurs";
  const entitiesLandingLabel = canViewClients ? "Clients" : "Fournisseurs";
  const newEntityActionLabel = canViewClients
    ? "nouveau client"
    : "nouveau fournisseur";
  const canViewProducts =
    hasPermission?.("Gestion Commerciale.Produits.Produits.view") ||
    hasPermission?.("Gestion Commerciale.Produits.Categories.view") ||
    hasPermission?.("Gestion Commerciale.Produits.Marques.view") ||
    hasPermission?.("Gestion Commerciale.Produits.Unites.view") ||
    hasPermission?.("Gestion Commerciale.Services.view") ||
    hasPermission?.("Gestion Commerciale.RendezVous.view");
  const canViewProductCatalog = hasPermission?.(
    "Gestion Commerciale.Produits.Produits.view"
  );
  const canViewPurchases =
    hasPermission?.("Gestion Commerciale.Approvisionnement.Achats.Achat.view") ||
    hasPermission?.(
      "Gestion Commerciale.Approvisionnement.Achats.BonCommande.view"
    ) ||
    hasPermission?.(
      "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.view"
    ) ||
    hasPermission?.(
      "Gestion Commerciale.Approvisionnement.Achats.PaiementsSortants.view"
    );
  const canViewProduction =
    hasPermission?.("Gestion Commerciale.Approvisionnement.Production.Gerer.view") ||
    hasPermission?.("Gestion Commerciale.Approvisionnement.Production.Unites.view") ||
    hasPermission?.(
      "Gestion Commerciale.Approvisionnement.Production.Historique.view"
    ) ||
    (selectedCompanyHasProfile(user, selectedCompany, "production") &&
      hasPermission?.(
        "Gestion Commerciale.Approvisionnement.Production.Materiels.view"
      ));
  const canViewKitchenProduction =
    selectedCompanyHasProfile(user, selectedCompany, "restaurant") &&
    hasPermission?.("Gestion Commerciale.Restauration.Cuisine.view");
  const productionLandingPath = "/production";
  const canViewRestaurantOrderTaking =
    selectedCompanyHasProfile(user, selectedCompany, "restaurant") &&
    hasPermission?.("Gestion Commerciale.Restauration.CommandesSalle.view");
  const canViewSalesTransactions = hasPermission?.(
    "Gestion Commerciale.Ventes.Ventes.view"
  );
  const canViewDeliveries = hasPermission?.(
    "Gestion Commerciale.Ventes.Livraisons.view"
  );
  const canViewStandardSales = canViewSalesTransactions || canViewDeliveries;
  const canViewSales = canViewStandardSales || canViewRestaurantOrderTaking;
  const salesLandingPath =
    canViewSalesTransactions
      ? "/gestion-ventes/ventes"
      : canViewRestaurantOrderTaking
      ? "/gestion-ventes/commandes-restaurant"
      : canViewDeliveries
        ? "/gestion-ventes/livraisons"
        : "/gestion-ventes";
  const productsLandingPath = canViewProductCatalog
    ? "/gestion-produits/produits"
    : "/gestion-produits";
  const salesActionLabel = canViewSalesTransactions
    ? "nouvelle vente"
    : "nouvelle prise de commande";
  const canViewStock =
    hasPermission?.("Gestion Commerciale.Stock.GestionStock.Transfer.view") ||
    hasPermission?.("Gestion Commerciale.Stock.GestionStock.Adjust.view") ||
    hasPermission?.("Gestion Commerciale.Stock.GestionStock.History.view");
  const canViewTreasury = hasPermissionPrefix("Gestion Commerciale.Tresorerie.");
  const canViewEcommerce =
    hasPermission?.("Gestion Commerciale.Ecommerce.CommandesEnLigne.view") ||
    hasPermission?.(
      "Gestion Commerciale.Ecommerce.Configuration.Boutiques.view"
    ) ||
    hasPermission?.(
      "Gestion Commerciale.Ecommerce.Configuration.FichesProduit.view"
    ) ||
    hasPermission?.(
      "Gestion Commerciale.Ecommerce.Configuration.Frontend.view"
    );
  const canViewAdmin = hasPermissionPrefix("Admin.");
  const commercialCompanyId =
    selectedCompany || user?.company?.id || user?.company_id || null;
  const commercialBusinessDomain = normalizeSearchText(
    user?.company?.business_domain || "commercial"
  );
  const commercialActivityProfile = normalizeSearchText(
    user?.company?.activity_profile || ""
  );
  const canUseCommercialOnboarding =
    !user?.is_superadmin &&
    commercialCompanyId &&
    commercialBusinessDomain === "commercial" &&
    hasPermissionPrefix("Gestion Commerciale.");

  useEffect(() => {
    if (!canUseCommercialOnboarding || commercialActivityProfile) {
      return;
    }

    const dismissed = localStorage.getItem(
      buildCommercialOnboardingStorageKey(commercialCompanyId, "dismissed")
    );

    if (dismissed !== "true") {
      setCommercialOnboardingOpen(true);
    }
  }, [
    canUseCommercialOnboarding,
    commercialActivityProfile,
    commercialCompanyId,
  ]);

  const primaryShortcuts = useMemo(
    () =>
      [
        {
          key: "pos",
          title: "Point de vente (POS)",
          icon: <ShopOutlined />,
          path: "/gestion-pos",
          color: "#d97706",
          visible: canUsePos,
          disabled: !workspaceReady,
        },
        {
          key: "sales",
          title: "Ventes",
          icon: <ShoppingOutlined />,
          path: salesLandingPath,
          color: "#295d86",
          visible: canViewSales,
        },
        {
          key: "purchases",
          title: "Achats",
          icon: <ShoppingCartOutlined />,
          path: "/gestion-achats",
          color: "#c58a00",
          visible: canViewPurchases,
        },
        {
          key: "stock",
          title: "Stock",
          icon: <DropboxOutlined />,
          path: "/gestion-stock",
          color: "#2f9d62",
          visible: canViewStock,
        },
        {
          key: "products",
          title: "Produits",
          icon: <AppstoreOutlined />,
          path: productsLandingPath,
          color: "#6d4aff",
          visible: canViewProducts,
        },
        {
          key: "treasury",
          title: "Tresorerie",
          icon: <WalletOutlined />,
          path: "/caisse-banques",
          color: "#0f8ea8",
          visible: canViewTreasury,
        },
        {
          key: "restaurant",
          title: "Restaurant",
          icon: <CoffeeOutlined />,
          path: "/gestion-ventes/commandes-restaurant",
          color: "#be4349",
          visible: canViewRestaurantOrderTaking,
        },
        {
          key: "dashboard",
          title: "Tableau de bord",
          icon: <DashboardOutlined />,
          path: "/tableau-de-bord",
          color: "#295d86",
          visible: canViewDashboard,
        },
        {
          key: "entities",
          title: entitiesLandingLabel,
          icon: <TeamOutlined />,
          path: entitiesLandingPath,
          color: "#d14b7d",
          visible: canViewEntities,
        },
        {
          key: "production",
          title: productionTerminology.processTitle,
          icon: <ExperimentOutlined />,
          path: productionLandingPath,
          color: "#9a3412",
          visible: canViewProduction || canViewKitchenProduction,
        },
        {
          key: "ecommerce",
          title: "E-commerce",
          icon: <RocketOutlined />,
          path: "/gestion-ecommerce",
          color: "#c44747",
          visible: canViewEcommerce,
        },
        {
          key: "admin",
          title: "Admin",
          icon: <SettingOutlined />,
          path: "/gestion-entreprises",
          color: "#54657a",
          visible: canViewAdmin,
        },
      ].filter((item) => item.visible),
    [
      canUsePos,
      workspaceReady,
      salesLandingPath,
      canViewSales,
      canViewPurchases,
      canViewStock,
      canViewProducts,
      productsLandingPath,
      canViewTreasury,
      canViewRestaurantOrderTaking,
      canViewDashboard,
      canViewEntities,
      entitiesLandingLabel,
      entitiesLandingPath,
      productionTerminology.processTitle,
      productionLandingPath,
      canViewProduction,
      canViewKitchenProduction,
      canViewEcommerce,
      canViewAdmin,
    ]
  );

  const interfaceShortcuts = useMemo(
    () =>
      buildHomeInterfaceShortcuts(homeShortcutMenuTree, {
        reservedPaths: primaryShortcuts.map((shortcut) => shortcut.path),
      }),
    [homeShortcutMenuTree, primaryShortcuts],
  );

  const availableShortcuts = useMemo(
    () => [...primaryShortcuts, ...interfaceShortcuts],
    [interfaceShortcuts, primaryShortcuts],
  );

  const defaultShortcutKeys = useMemo(
    () => primaryShortcuts.map((shortcut) => shortcut.key),
    [primaryShortcuts],
  );

  const pinned = useMemo(
    () =>
      filterHomeShortcuts(
        availableShortcuts,
        shortcutPreferenceKeys,
        defaultShortcutKeys,
      ),
    [availableShortcuts, defaultShortcutKeys, shortcutPreferenceKeys],
  );

  const shortcutGroups = useMemo(() => {
    const groups = [
      {
        key: "primary",
        title: "Modules principaux",
        items: primaryShortcuts,
      },
    ];
    const interfaceGroups = new Map();

    interfaceShortcuts.forEach((shortcut) => {
      if (!interfaceGroups.has(shortcut.groupKey)) {
        interfaceGroups.set(shortcut.groupKey, {
          key: shortcut.groupKey,
          title: shortcut.groupTitle,
          items: [],
        });
      }
      interfaceGroups.get(shortcut.groupKey).items.push(shortcut);
    });

    return [
      ...groups.filter((group) => group.items.length > 0),
      ...interfaceGroups.values(),
    ];
  }, [interfaceShortcuts, primaryShortcuts]);

  const filteredShortcutGroups = useMemo(() => {
    const query = normalizeSearchText(shortcutSearchQuery);
    if (!query) return shortcutGroups;

    return shortcutGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((shortcut) =>
          normalizeSearchText(
            `${group.title} ${shortcut.contextLabel || ""} ${shortcut.title}`,
          ).includes(query),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [shortcutGroups, shortcutSearchQuery]);

  const openShortcutCustomizer = async () => {
    let preferenceKeys = shortcutPreferenceKeys;

    if (shortcutPreferenceStatus === "error") {
      const result = await loadShortcutPreference();
      if (!result.loaded) {
        message.error(
          "Impossible de charger vos préférences de raccourcis pour le moment.",
        );
        return;
      }
      preferenceKeys = result.keys;
    }

    const availableKeys = new Set(
      availableShortcuts.map((shortcut) => shortcut.key),
    );
    const initialKeys = Array.isArray(preferenceKeys)
      ? preferenceKeys
      : defaultShortcutKeys;

    setShortcutDraftKeys(initialKeys.filter((key) => availableKeys.has(key)));
    setShortcutSearchQuery("");
    setShortcutCustomizerOpen(true);
  };

  const toggleShortcutDraft = (key) => {
    setShortcutDraftKeys((current) =>
      current.includes(key)
        ? current.filter((currentKey) => currentKey !== key)
        : [...current, key],
    );
  };

  const saveShortcutPreference = async () => {
    if (!selectedCompany || shortcutSaving) return;
    const preferenceCompanyId = selectedCompany;

    const shortcutKeys = mergeHomeShortcutSelection({
      availableShortcuts,
      preferenceKeys: shortcutPreferenceKeys,
      selectedKeys: shortcutDraftKeys,
    });

    setShortcutSaving(true);
    try {
      const { data } = await apiClient.put("/api/home-shortcuts", {
        company_id: preferenceCompanyId,
        shortcut_keys: shortcutKeys,
      });
      if (
        String(selectedCompanyRef.current) !== String(preferenceCompanyId)
      ) {
        return;
      }
      const savedKeys = normalizeHomeShortcutPreference(data?.preference);
      setShortcutPreferenceKeys(
        Array.isArray(savedKeys) ? savedKeys : shortcutKeys,
      );
      setShortcutPreferenceStatus("ready");
      setShortcutCustomizerOpen(false);
      message.success("Vos raccourcis ont été mis à jour.");
    } catch (error) {
      if (
        String(selectedCompanyRef.current) === String(preferenceCompanyId)
      ) {
        message.error(
          error?.response?.data?.error ||
            "Impossible d’enregistrer vos raccourcis pour le moment.",
        );
      }
    } finally {
      setShortcutSaving(false);
    }
  };

  const resetShortcutPreference = async () => {
    if (!selectedCompany || shortcutSaving) return;
    const preferenceCompanyId = selectedCompany;

    if (!Array.isArray(shortcutPreferenceKeys)) {
      setShortcutDraftKeys(defaultShortcutKeys);
      return;
    }

    setShortcutSaving(true);
    try {
      await apiClient.delete("/api/home-shortcuts", {
        params: { company_id: preferenceCompanyId },
      });
      if (
        String(selectedCompanyRef.current) !== String(preferenceCompanyId)
      ) {
        return;
      }
      setShortcutPreferenceKeys(null);
      setShortcutPreferenceStatus("ready");
      setShortcutDraftKeys(defaultShortcutKeys);
      setShortcutCustomizerOpen(false);
      message.success("Les raccourcis par défaut ont été restaurés.");
    } catch (error) {
      if (
        String(selectedCompanyRef.current) === String(preferenceCompanyId)
      ) {
        message.error(
          error?.response?.data?.error ||
            "Impossible de réinitialiser vos raccourcis pour le moment.",
        );
      }
    } finally {
      setShortcutSaving(false);
    }
  };

  const suggestions = useMemo(
    () =>
      [
        {
          key: "sale",
          kbd: "V",
          label: salesActionLabel,
          icon: <ShoppingOutlined />,
          path: salesLandingPath,
          visible: canViewSalesTransactions || canViewRestaurantOrderTaking,
        },
        {
          key: "product",
          kbd: "P",
          label: "nouveau produit",
          icon: <AppstoreOutlined />,
          path: productsLandingPath,
          visible: canViewProductCatalog,
        },
        {
          key: "client",
          kbd: "C",
          label: newEntityActionLabel,
          icon: <TeamOutlined />,
          path: entitiesLandingPath,
          visible: canViewEntities,
        },
        {
          key: "stock",
          kbd: "I",
          label: "inventaire rapide",
          icon: <FileTextOutlined />,
          path: "/gestion-stock",
          visible: canViewStock,
        },
      ].filter((item) => item.visible),
    [
      canViewEntities,
      entitiesLandingPath,
      canViewProductCatalog,
      newEntityActionLabel,
      productsLandingPath,
      canViewRestaurantOrderTaking,
      canViewSalesTransactions,
      canViewStock,
      salesActionLabel,
      salesLandingPath,
    ]
  );

  const commandGroups = useMemo(
    () => [
      {
        key: "actions",
        label: "Actions rapides",
        items: suggestions.map((suggestion) => ({
          id: `action-${suggestion.key}`,
          type: "action",
          title: suggestion.label,
          meta: "Action rapide",
          path: suggestion.path,
          icon: suggestion.icon,
          shortcut: suggestion.kbd,
          searchText: `${suggestion.label} action rapide ${suggestion.kbd}`,
        })),
      },
      {
        key: "modules",
        label: "Modules",
        items: primaryShortcuts.map((item) => ({
          id: `module-${item.key}`,
          type: "module",
          title: item.title,
          meta: item.disabled ? "Contexte requis" : "Ouvrir le module",
          path: item.path,
          icon: item.icon,
          disabled: item.disabled,
          searchText: `${item.title} module raccourci`,
        })),
      },
    ],
    [primaryShortcuts, suggestions]
  );

  const filteredCommandGroups = useMemo(() => {
    const query = normalizeSearchText(commandQuery);

    return commandGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (!query) return true;
          return normalizeSearchText(item.searchText).includes(query);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [commandGroups, commandQuery]);

  const remoteCommandGroups = useMemo(() => {
    if (normalizeSearchText(commandQuery).length < 2) return [];

    return commandSearchGroups.map((group) => ({
      ...group,
      key: `remote-${group.key}`,
      items: group.items.map((item) => ({
        ...item,
        id: `remote-${item.id}`,
        icon: getCommandIconForType(item.type),
        searchText: `${item.title} ${item.meta} ${item.amount}`,
        remote: true,
      })),
    }));
  }, [commandQuery, commandSearchGroups]);

  const visibleCommandGroups = useMemo(
    () => [...filteredCommandGroups, ...remoteCommandGroups],
    [filteredCommandGroups, remoteCommandGroups]
  );

  const flatCommandResults = useMemo(
    () => visibleCommandGroups.flatMap((group) => group.items),
    [visibleCommandGroups]
  );

  useEffect(() => {
    if (!commandOpen) return;
    setCommandActiveIndex((currentIndex) => {
      if (flatCommandResults.length === 0) return 0;
      return Math.min(currentIndex, flatCommandResults.length - 1);
    });
  }, [commandOpen, flatCommandResults.length]);

  useEffect(() => {
    if (!commandOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        commandPaletteRef.current &&
        !commandPaletteRef.current.contains(event.target)
      ) {
        setCommandOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, [commandOpen]);

  useEffect(() => {
    const handleGlobalCommandKeys = (event) => {
      const isEditableTarget =
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA" ||
          event.target.isContentEditable);

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
        commandInputRef.current?.focus();
        commandInputRef.current?.select();
        return;
      }

      if (isEditableTarget || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const shortcut = suggestions.find(
        (suggestion) => suggestion.kbd.toLowerCase() === event.key.toLowerCase()
      );

      if (shortcut) {
        event.preventDefault();
        navigate(shortcut.path);
      }
    };

    window.addEventListener("keydown", handleGlobalCommandKeys);
    return () => window.removeEventListener("keydown", handleGlobalCommandKeys);
  }, [navigate, suggestions]);

  const selectCommandItem = (item) => {
    if (!item || item.disabled) return;
    navigate(item.path);
    setCommandOpen(false);
    setCommandQuery("");
    setCommandActiveIndex(0);
    commandInputRef.current?.blur();
  };

  const handleCommandKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCommandOpen(true);
      setCommandActiveIndex((currentIndex) =>
        flatCommandResults.length
          ? (currentIndex + 1) % flatCommandResults.length
          : 0
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setCommandOpen(true);
      setCommandActiveIndex((currentIndex) =>
        flatCommandResults.length
          ? (currentIndex - 1 + flatCommandResults.length) %
            flatCommandResults.length
          : 0
      );
      return;
    }

    if (event.key === "Enter") {
      if (!commandOpen) return;
      event.preventDefault();
      selectCommandItem(flatCommandResults[commandActiveIndex]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setCommandOpen(false);
      setCommandActiveIndex(0);
      commandInputRef.current?.blur();
    }
  };

  const openAgendaItem = (item) => {
    setSelectedAgendaItem(item);
    setAgendaModalOpen(true);

    if (!item.is_read) {
      setAgendaItems((current) =>
        current.map((agendaItem) =>
          agendaItem.id === item.id ? { ...agendaItem, is_read: true } : agendaItem
        )
      );
      apiClient
        .post(`/api/notifications/mark-read/${item.id}`, {
          company_id: selectedCompany,
          ...(selectedWarehouse ? { warehouse_id: selectedWarehouse } : {}),
        })
        .catch(() => undefined);
    }
  };

  const updateAgendaState = async (status, snoozedUntil = null) => {
    if (!selectedAgendaItem || !selectedCompany || agendaUpdatingId) return;

    try {
      setAgendaUpdatingId(selectedAgendaItem.id);
      await apiClient.patch(
        `/api/agenda/notifications/${selectedAgendaItem.id}/state`,
        {
          company_id: selectedCompany,
          ...(selectedWarehouse ? { warehouse_id: selectedWarehouse } : {}),
          status,
          ...(snoozedUntil ? { snoozed_until: snoozedUntil.toISOString() } : {}),
        }
      );

      setAgendaItems((current) =>
        current.filter((item) => item.id !== selectedAgendaItem.id)
      );
      setAgendaTotal((current) => Math.max(0, current - 1));
      setAgendaModalOpen(false);
      setSelectedAgendaItem(null);
      message.success(
        status === "snoozed"
          ? "Priorité reportée à demain"
          : "Priorité retirée de l’agenda"
      );
      loadAgenda({ silent: true });
    } catch (error) {
      message.error(
        error?.response?.data?.error || "Impossible de mettre à jour l’agenda"
      );
    } finally {
      setAgendaUpdatingId(null);
    }
  };

  const snoozeSelectedAgendaItem = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(8, 0, 0, 0);
    updateAgendaState("snoozed", tomorrow);
  };

  const isDailyRecap = selectedAgendaItem?.type === "daily_recap";
  const agendaDetailRows = useMemo(
    () =>
      selectedAgendaItem && selectedAgendaItem.type !== "daily_recap"
        ? getNotificationDetailRows(
            selectedAgendaItem.type,
            selectedAgendaItem.data || {}
          )
        : [],
    [selectedAgendaItem]
  );
  const initialsOf = (name) =>
    String(name || "?")
      .split(/\s+/)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const kpiDeltaIsNegative = kpiToday.deltaValue < 0;
  const pulseChartData = kpiToday.hourly.map((value, hour) => ({
    hour,
    Ventes: toFiniteNumber(value),
  }));
  const chartHasData = pulseChartData.some((point) => point.Ventes > 0);
  const onlineAvatars = teamActivity.onlineUsers.slice(0, 4);
  const teamFeed = teamActivity.feed;
  const activityModuleOptions =
    activityHistory.modules.length > 0
      ? activityHistory.modules
      : teamActivity.modules;
  const activityWarehouseLabel = activityFilters.warehouseId
    ? warehousesForSelectedCompany.find(
        (warehouse) =>
          String(warehouse.id) === String(activityFilters.warehouseId),
      )?.name || `Établissement #${activityFilters.warehouseId}`
    : "Tous mes établissements";
  const activityFiltersAreActive =
    activityFilters.module !== "all" ||
    activityFilters.actorScope !== "all" ||
    Boolean(activityFilters.userId) ||
    String(activityFilters.warehouseId || "") !==
      String(selectedWarehouse || "") ||
    Boolean(activityFilters.startDate) ||
    Boolean(activityFilters.endDate);
  const commandQueryIsSearchable = normalizeSearchText(commandQuery).length >= 2;
  const commandEmptyMessage =
    commandSearchStatus === "loading" && commandQueryIsSearchable
      ? "Recherche dans les donnees du contexte..."
      : commandSearchStatus === "error" && commandQueryIsSearchable
        ? "Recherche metier indisponible pour le moment."
        : `Aucun resultat pour "${commandQuery}".`;
  const showCommandSearchStatus =
    commandQueryIsSearchable &&
    flatCommandResults.length > 0 &&
    (commandSearchStatus === "loading" || commandSearchStatus === "error");

  return (
    <div className="home-c">
      <div className="home-c__mobile-context">
        <MobileContextSelectors />
      </div>

      <div className="home-c__greet">
        <div className="home-c__intro">
          <div className="home-c__date">
            {dateLabel} - {timeLabel}
          </div>
          <h1 className="home-c__title">
            {getGreeting(now.getHours())} {firstName}.{" "}
            <span className="home-c__title-sub">Que voulez-vous faire ?</span>
          </h1>
        </div>

        <div className="home-c__status-pills">
          <span
            className={`home-c__pill ${
              workspaceReady ? "home-c__pill--good" : "home-c__pill--warn"
            }`}
          >
            <span className="home-c__dot" />
            {workspaceReady ? "Contexte actif" : "Contexte requis"}
          </span>
          <span className="home-c__pill home-c__pill--clip">{companyName}</span>
          <span className="home-c__pill home-c__pill--clip">{warehouseName}</span>
          <span className="home-c__pill">
            <CalendarOutlined className="home-c__pill-icon" />
            {agendaStatus === "forbidden"
              ? "Agenda non autorisé"
              : agendaStatus === "error"
              ? "Agenda indisponible"
              : agendaTotal === 0
                ? "Agenda à jour"
                : `${agendaTotal} priorité${agendaTotal > 1 ? "s" : ""} aujourd’hui`}
          </span>
          {canUseCommercialOnboarding && (
            <button
              type="button"
              className="home-c__assistant-button"
              onClick={() => setCommercialOnboardingOpen(true)}
            >
              <RocketOutlined className="home-c__pill-icon" />
              Assistant
            </button>
          )}
        </div>
      </div>

      <div className="home-c__palette-wrap" ref={commandPaletteRef}>
        <label
          className={`home-c__palette ${commandOpen ? "home-c__palette--open" : ""}`}
        >
          <SearchOutlined className="home-c__palette-icon" />
          <input
            ref={commandInputRef}
            type="text"
            value={commandQuery}
            placeholder="Tapez une action, un client, un produit, un numero de ticket..."
            aria-label="Centre de commande"
            aria-controls="home-command-results"
            aria-expanded={commandOpen}
            aria-activedescendant={
              flatCommandResults[commandActiveIndex]?.id || undefined
            }
            autoComplete="off"
            role="combobox"
            onChange={(event) => {
              setCommandQuery(event.target.value);
              setCommandOpen(true);
              setCommandActiveIndex(0);
            }}
            onFocus={() => setCommandOpen(true)}
            onKeyDown={handleCommandKeyDown}
          />
          <kbd>{commandKey}</kbd>
        </label>

        {commandOpen && (
          <div
            id="home-command-results"
            className="home-c__command-panel"
            role="listbox"
            aria-label="Resultats du centre de commande"
            onMouseDown={(event) => event.preventDefault()}
          >
            {flatCommandResults.length > 0 ? (
              visibleCommandGroups.map((group) => (
                <div key={group.key} className="home-c__command-group">
                  <div className="home-c__command-group-title">{group.label}</div>
                  <div className="home-c__command-list">
                    {group.items.map((item) => {
                      const itemIndex = flatCommandResults.findIndex(
                        (result) => result.id === item.id
                      );
                      const isActive = itemIndex === commandActiveIndex;

                      return (
                        <button
                          key={item.id}
                          id={item.id}
                          type="button"
                          className={`home-c__command-item ${
                            isActive ? "home-c__command-item--active" : ""
                          } ${item.disabled ? "home-c__command-item--disabled" : ""} ${
                            item.remote ? "home-c__command-item--remote" : ""
                          }`}
                          role="option"
                          aria-selected={isActive}
                          aria-disabled={item.disabled || undefined}
                          onMouseEnter={() => setCommandActiveIndex(itemIndex)}
                          onClick={() => selectCommandItem(item)}
                        >
                          <span className="home-c__command-icon">{item.icon}</span>
                          <span className="home-c__command-copy">
                            <strong>{item.title}</strong>
                            <small>{item.meta}</small>
                          </span>
                          {item.shortcut ? (
                            <kbd>{item.shortcut}</kbd>
                          ) : (
                            <span
                              className={`home-c__command-enter ${
                                item.amount ? "home-c__command-amount" : ""
                              }`}
                            >
                              {item.amount || "Entree"}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="home-c__command-empty">{commandEmptyMessage}</div>
            )}

            {showCommandSearchStatus && (
              <div
                className={`home-c__command-status home-c__command-status--${commandSearchStatus}`}
              >
                {commandSearchStatus === "loading"
                  ? "Recherche dans les donnees du contexte..."
                  : "Recherche metier indisponible pour le moment."}
              </div>
            )}
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="home-c__suggestions">
            <span className="home-c__suggestions-label">Suggestions</span>
            {suggestions.map((suggestion) => (
              <Link
                key={suggestion.key}
                to={suggestion.path}
                className="home-c__suggestion"
              >
                {suggestion.icon}
                <strong>{suggestion.label}</strong>
                <kbd>{suggestion.kbd}</kbd>
              </Link>
            ))}
          </div>
        )}
      </div>

      <section className="home-c__section" aria-labelledby="home-shortcuts-title">
        <div className="home-c__section-head">
          <h2 id="home-shortcuts-title" className="home-c__section-title">
            Mes raccourcis
            <span className="home-c__section-title-soft">
              {" "}
              - {pinned.length} affiché{pinned.length !== 1 ? "s" : ""} sur{" "}
              {availableShortcuts.length} disponible
              {availableShortcuts.length !== 1 ? "s" : ""}
            </span>
          </h2>
          <Button
            size="small"
            icon={<SettingOutlined />}
            className="home-c__shortcut-customize"
            loading={shortcutPreferenceStatus === "loading"}
            disabled={!selectedCompany || availableShortcuts.length === 0}
            onClick={() => void openShortcutCustomizer()}
          >
            Personnaliser
          </Button>
        </div>

        {pinned.length > 0 ? (
          <div className="home-c__pinned">
            {pinned.map((item) => {
              const pinContent = (
                <>
                  <span
                    className="home-c__pin-icon"
                    style={{
                      background: `color-mix(in srgb, ${item.color} 12%, white)`,
                      color: item.color,
                    }}
                  >
                    {item.icon}
                  </span>
                  <span className="home-c__pin-label">{item.title}</span>
                </>
              );

              if (item.disabled) {
                return (
                  <div
                    key={item.key}
                    className="home-c__pin home-c__pin--disabled"
                    aria-disabled="true"
                  >
                    {pinContent}
                  </div>
                );
              }

              return (
                <Link key={item.key} to={item.path} className="home-c__pin">
                  {pinContent}
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="home-c__empty">
            {availableShortcuts.length > 0
              ? "Aucun raccourci sélectionné. Utilisez « Personnaliser » pour en ajouter."
              : "Aucun raccourci disponible avec les permissions actuelles."}
          </div>
        )}
      </section>

      <div className="home-c__grid3">
        <section className="home-c__card home-c__pulse" aria-labelledby="home-pulse-title">
          <div className="home-c__pulse-head">
            <div>
              <div className="home-c__eyebrow">Pulse - Ventes</div>
              <h2 id="home-pulse-title" className="home-c__pulse-value">
                {formatXof(kpiToday.ca)}{" "}
                <small>FCFA - aujourd'hui</small>
              </h2>
            </div>
            <span
              className={`home-c__delta ${
                kpiDeltaIsNegative ? "home-c__delta--down" : ""
              }`}
            >
              {kpiDeltaIsNegative ? (
                <ArrowDownOutlined className="home-c__delta-icon" />
              ) : (
                <ArrowUpOutlined className="home-c__delta-icon" />
              )}
              {kpiToday.delta}
            </span>
          </div>

          <div className="home-c__pulse-chart">
            {kpiStatus === "loading" ? (
              <div className="home-c__chart-placeholder">Chargement du flux...</div>
            ) : chartHasData ? (
              <ResponsiveContainer width="100%" height={128}>
                <LineChart
                  data={pulseChartData}
                  margin={{ top: 8, right: 8, bottom: 0, left: -18 }}
                >
                  <CartesianGrid stroke="var(--border-color)" vertical={false} />
                  <XAxis
                    dataKey="hour"
                    type="number"
                    domain={[0, 23]}
                    ticks={[0, 6, 12, 18, 23]}
                    tickFormatter={formatPulseHourTick}
                    tick={{ fontSize: 10, fill: "var(--text-tertiary)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={formatYAxisTick}
                    tick={{ fontSize: 10, fill: "var(--text-tertiary)" }}
                    axisLine={false}
                    tickLine={false}
                    width={38}
                  />
                  <RechartsTooltip
                    formatter={chartTooltipFormatter}
                    labelFormatter={chartTooltipLabelFormatter}
                    contentStyle={{
                      border: "1px solid var(--border-color)",
                      borderRadius: 8,
                      background: "var(--bg-primary)",
                      color: "var(--text-primary)",
                      fontSize: 12,
                    }}
                  />
                  <RechartsLine
                    type="monotone"
                    dataKey="Ventes"
                    stroke="#3f8600"
                    strokeWidth={2.4}
                    dot={false}
                    name="Ventes"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="home-c__chart-placeholder">
                Aucune vente enregistree aujourd'hui.
              </div>
            )}
            <div className="home-c__chart-legend">
              <span>
                <i className="home-c__legend-dot home-c__legend-dot--sales" />
                Ventes
              </span>
              <span>00h - 23h59</span>
            </div>
          </div>

          <div className="home-c__pulse-stats">
            <div className="home-c__pulse-stat">
              <strong>{kpiToday.tickets}</strong>
              <span>tickets</span>
            </div>
            <div className="home-c__pulse-stat">
              <strong>{formatXof(kpiToday.panierMoyen)} F</strong>
              <span>panier moyen</span>
            </div>
            <div className="home-c__pulse-stat">
              <strong>{kpiToday.cashShare}</strong>
              <span>en especes</span>
            </div>
          </div>

          {kpiStatus === "loading" && (
            <div className="home-c__pulse-note">Synchronisation des ventes...</div>
          )}
          {kpiStatus === "error" && (
            <div className="home-c__pulse-note home-c__pulse-note--error">
              Donnees ventes indisponibles.
            </div>
          )}
          {!selectedCompany && (
            <div className="home-c__pulse-note">
              Selectionnez une entreprise pour afficher le pulse.
            </div>
          )}
        </section>

        <section className="home-c__card" aria-labelledby="home-agenda-title">
          <div className="home-c__card-head">
            <div>
              <div className="home-c__eyebrow">Aujourd'hui</div>
              <h2 id="home-agenda-title" className="home-c__h3">
                Agenda
              </h2>
            </div>
            {canViewAgenda && (
              <button
                type="button"
                className="home-c__card-link"
                onClick={() => navigate("/notifications")}
              >
                Tout voir
              </button>
            )}
          </div>

          {agendaStatus === "loading" && (
            <div className="home-c__row">
              <div className="home-c__row-body">
                <div className="home-c__row-title">Synchronisation de l’agenda...</div>
                <div className="home-c__row-meta">Recherche des priorités du contexte</div>
              </div>
            </div>
          )}

          {agendaStatus === "forbidden" && (
            <div className="home-c__empty home-c__empty--inline">
              La permission de consulter les notifications est requise.
            </div>
          )}

          {agendaStatus !== "loading" &&
            agendaStatus !== "forbidden" &&
            agendaItems.length === 0 &&
            agendaStatus !== "error" && (
            <div className="home-c__empty home-c__empty--inline">
              Aucune action prioritaire. Votre agenda est à jour.
            </div>
          )}

          {agendaItems.map((event) => (
            <button
              key={event.id}
              type="button"
              className="home-c__row home-c__agenda-row"
              onClick={() => openAgendaItem(event)}
            >
              <span className="home-c__row-time">{event.time_label}</span>
              <span className={`home-c__tone home-c__tone--${event.tone}`} />
              <span className="home-c__row-body">
                <span className="home-c__row-title">{event.title}</span>
                <span className="home-c__row-meta">
                  {event.category_label}
                  {event.kind === "advice"
                    ? " · Conseil"
                    : event.kind === "info"
                      ? " · Information"
                      : " · À faire"}
                  {event.warehouse_name ? ` · ${event.warehouse_name}` : ""}
                </span>
              </span>
              <RightOutlined className="home-c__agenda-chevron" aria-hidden="true" />
            </button>
          ))}

          {agendaStatus === "error" && (
            <div className="home-c__pulse-note home-c__pulse-note--error home-c__team-note">
              Agenda indisponible pour le moment.
            </div>
          )}
        </section>

        <section className="home-c__card" aria-labelledby="home-team-title">
          <div className="home-c__card-head">
            <div>
              <div className="home-c__eyebrow">Équipe</div>
              <h2 id="home-team-title" className="home-c__h3">
                Activité de l’équipe
              </h2>
              <div className="home-c__team-status">
                <span
                  className={`home-c__team-status-dot${
                    teamActivity.connectedCount === 0
                      ? " home-c__team-status-dot--offline"
                      : ""
                  }`}
                />
                {teamActivity.connectedCount} connecté
                {teamActivity.connectedCount > 1 ? "s" : ""}
              </div>
            </div>
            <div className="home-c__card-actions">
              {onlineAvatars.length > 0 && (
                <div className="home-c__avatars" aria-hidden="true">
                  {onlineAvatars.map((avatar, index) => (
                    <span
                      key={avatar.id || `${avatar.name}-${index}`}
                      style={{
                        background: ["#295d86", "#be4349", "#2f9d62", "#d97706"][
                          index % 4
                        ],
                      }}
                    >
                      {initialsOf(avatar.name)}
                    </span>
                  ))}
                </div>
              )}
              {teamActivity.modules.length > 0 && (
                <button
                  type="button"
                  className="home-c__card-link"
                  onClick={() => setActivityDrawerOpen(true)}
                >
                  Tout voir
                </button>
              )}
            </div>
          </div>

          {teamStatus === "loading" && (
            <div className="home-c__row">
              <div className="home-c__row-body">
                <div className="home-c__row-title">Synchronisation de l’activité...</div>
                <div className="home-c__row-meta">Contexte équipe</div>
              </div>
            </div>
          )}

          {teamStatus !== "loading" && teamFeed.length === 0 && (
            <div className="home-c__empty home-c__empty--inline">
              {teamActivity.modules.length === 0
                ? "Aucune activité accessible avec vos permissions."
                : "Aucune activité récente de l’équipe. Les interventions ELSA restent disponibles dans « Tout voir »."}
            </div>
          )}

          {teamFeed.map((feed, index) => (
            <div key={`${feed.id}-${index}`} className="home-c__row">
              <span
                className="home-c__activity-icon"
                style={{
                  color: feed.color || "#64748b",
                  background: `color-mix(in srgb, ${
                    feed.color || "#64748b"
                  } 12%, transparent)`,
                }}
                aria-hidden="true"
              >
                {getActivityIcon(feed.categoryKey)}
              </span>
              <div className="home-c__row-body">
                <div className="home-c__row-title">
                  <strong>{feed.actorName || "Système"}</strong>{" "}
                  {getActivitySourceLabel(feed) && (
                    <span className="home-c__activity-source-badge">
                      {getActivitySourceLabel(feed)}
                    </span>
                  )}{" "}
                  <span className="home-c__row-muted">- {feed.actionLabel}</span>
                </div>
                <div className="home-c__row-meta">
                  {feed.module} · {formatRelativeTime(feed.occurredAt, now)}
                  {feed.warehouseName ? ` · ${feed.warehouseName}` : ""}
                </div>
              </div>
              {formatActivityAmount(feed) && (
                <span className="home-c__row-amount">
                  {formatActivityAmount(feed)}
                </span>
              )}
            </div>
          ))}

          {teamStatus === "error" && (
            <div className="home-c__pulse-note home-c__pulse-note--error home-c__team-note">
              Activité récente indisponible.
            </div>
          )}
        </section>
      </div>

      <Drawer
        open={activityDrawerOpen}
        title={
          <div className="home-c__activity-drawer-title">
            <span className="home-c__eyebrow">Entreprise</span>
            <strong>Historique d’activité</strong>
            <small>
              Activité de l’équipe et interventions ELSA accessibles selon vos permissions
            </small>
          </div>
        }
        width={720}
        rootClassName="home-c__activity-drawer"
        onClose={() => setActivityDrawerOpen(false)}
      >
        <div className="home-c__activity-context">
          <span>{companyName}</span>
          <span aria-hidden="true">·</span>
          <span>{activityWarehouseLabel}</span>
        </div>

        <div className="home-c__activity-filters">
          <div>
            <span className="home-c__activity-filter-label">Module</span>
            <div
              className="home-c__activity-module-filters"
              role="group"
              aria-label="Filtrer par module"
            >
              <button
                type="button"
                className={
                  activityFilters.module === "all"
                    ? "home-c__activity-filter-chip home-c__activity-filter-chip--active"
                    : "home-c__activity-filter-chip"
                }
                onClick={() =>
                  setActivityFilters((current) => ({
                    ...current,
                    module: "all",
                  }))
                }
              >
                Tous
              </button>
              {activityModuleOptions.map((module) => (
                <button
                  key={module.key}
                  type="button"
                  className={
                    activityFilters.module === module.key
                      ? "home-c__activity-filter-chip home-c__activity-filter-chip--active"
                      : "home-c__activity-filter-chip"
                  }
                  style={{ "--activity-color": module.color }}
                  onClick={() =>
                    setActivityFilters((current) => ({
                      ...current,
                      module: module.key,
                    }))
                  }
                >
                  <span
                    className="home-c__activity-filter-dot"
                    aria-hidden="true"
                  />
                  {module.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="home-c__activity-filter-label">Origine</span>
            <div
              className="home-c__activity-module-filters"
              role="group"
              aria-label="Filtrer par origine"
            >
              {ACTIVITY_ORIGIN_OPTIONS.map((scope) => (
                <button
                  key={scope.key}
                  type="button"
                  className={
                    activityFilters.actorScope === scope.key
                      ? "home-c__activity-filter-chip home-c__activity-filter-chip--active"
                      : "home-c__activity-filter-chip"
                  }
                  style={{ "--activity-color": scope.color }}
                  onClick={() =>
                    setActivityFilters((current) => ({
                      ...current,
                      actorScope: scope.key,
                      userId: "",
                    }))
                  }
                >
                  <span
                    className="home-c__activity-filter-dot"
                    aria-hidden="true"
                  />
                  {scope.label}
                </button>
              ))}
            </div>
          </div>

          <div className="home-c__activity-filter-grid">
            <label>
              <span className="home-c__activity-filter-label">Utilisateur</span>
              <select
                value={activityFilters.userId}
                onChange={(event) =>
                  setActivityFilters((current) => ({
                    ...current,
                    userId: event.target.value,
                  }))
                }
              >
                <option value="">Tous les utilisateurs</option>
                {activityHistory.actors.map((actor) => (
                  <option key={actor.id} value={actor.id}>
                    {getActivityActorOptionLabel(actor)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="home-c__activity-filter-label">Établissement</span>
              <select
                value={activityFilters.warehouseId}
                onChange={(event) =>
                  setActivityFilters((current) => ({
                    ...current,
                    warehouseId: event.target.value,
                  }))
                }
              >
                <option value="">Tous mes établissements</option>
                {warehousesForSelectedCompany.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name || `Établissement #${warehouse.id}`}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="home-c__activity-filter-label">Du</span>
              <input
                type="date"
                value={activityFilters.startDate}
                max={activityFilters.endDate || undefined}
                onChange={(event) =>
                  setActivityFilters((current) => ({
                    ...current,
                    startDate: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              <span className="home-c__activity-filter-label">Au</span>
              <input
                type="date"
                value={activityFilters.endDate}
                min={activityFilters.startDate || undefined}
                onChange={(event) =>
                  setActivityFilters((current) => ({
                    ...current,
                    endDate: event.target.value,
                  }))
                }
              />
            </label>
            <Button
              disabled={!activityFiltersAreActive}
              onClick={() =>
                setActivityFilters({
                  module: "all",
                  actorScope: "all",
                  userId: "",
                  warehouseId: selectedWarehouse
                    ? String(selectedWarehouse)
                    : "",
                  startDate: "",
                  endDate: "",
                })
              }
            >
              Réinitialiser
            </Button>
          </div>
        </div>

        <div className="home-c__activity-history" aria-live="polite">
          {activityHistoryStatus === "loading" && (
            <div className="home-c__activity-loading">
              Chargement de l’historique…
            </div>
          )}

          {activityHistoryStatus === "error" && (
            <div className="home-c__activity-error">
              <p>L’historique d’activité est indisponible pour le moment.</p>
              <Button onClick={() => loadActivityHistory()}>Réessayer</Button>
            </div>
          )}

          {activityHistoryStatus === "ready" &&
            activityHistory.feed.length === 0 && (
              <div className="home-c__empty">
                Aucune activité ne correspond aux filtres sélectionnés.
              </div>
            )}

          {activityHistory.feed.map((feed) => (
            <button
              key={feed.id}
              type="button"
              className="home-c__activity-history-row"
              onClick={() => {
                if (!feed.path?.startsWith("/")) return;
                setActivityDrawerOpen(false);
                navigate(feed.path);
              }}
            >
              <span
                className="home-c__activity-icon home-c__activity-icon--large"
                style={{
                  color: feed.color || "#64748b",
                  background: `color-mix(in srgb, ${
                    feed.color || "#64748b"
                  } 12%, transparent)`,
                }}
                aria-hidden="true"
              >
                {getActivityIcon(feed.categoryKey)}
              </span>
              <span className="home-c__activity-history-body">
                <span className="home-c__activity-history-title">
                  <strong>{feed.actorName || "Système"}</strong>{" "}
                  {getActivitySourceLabel(feed) && (
                    <span className="home-c__activity-source-badge">
                      {getActivitySourceLabel(feed)}
                    </span>
                  )}{" "}
                  {feed.actionLabel}
                </span>
                <span className="home-c__activity-history-meta">
                  <span
                    className="home-c__activity-module-badge"
                    style={{ "--activity-color": feed.color || "#64748b" }}
                  >
                    {feed.module}
                  </span>
                  <span>{formatRelativeTime(feed.occurredAt, now)}</span>
                  {feed.warehouseName && <span>{feed.warehouseName}</span>}
                </span>
              </span>
              {formatActivityAmount(feed) && (
                <span className="home-c__row-amount">
                  {formatActivityAmount(feed)}
                </span>
              )}
              <RightOutlined
                className="home-c__agenda-chevron"
                aria-hidden="true"
              />
            </button>
          ))}

          {activityHistoryStatus === "ready" &&
            activityHistory.pagination.hasMore && (
              <Button
                block
                loading={activityLoadingMore}
                className="home-c__activity-more"
                onClick={() =>
                  loadActivityHistory({
                    append: true,
                    cursor: activityHistory.pagination.nextCursor,
                  })
                }
              >
                Afficher 20 activités supplémentaires
              </Button>
            )}
        </div>
      </Drawer>

      <Modal
        open={shortcutCustomizerOpen}
        title="Personnaliser mes raccourcis"
        width={640}
        className="home-c__shortcut-modal"
        closable={!shortcutSaving}
        keyboard={!shortcutSaving}
        maskClosable={!shortcutSaving}
        onCancel={() => {
          if (!shortcutSaving) setShortcutCustomizerOpen(false);
        }}
        footer={
          <div className="home-c__shortcut-footer">
            <Button
              disabled={shortcutSaving}
              onClick={() => void resetShortcutPreference()}
            >
              Réinitialiser
            </Button>
            <div className="home-c__shortcut-footer-actions">
              <Button
                disabled={shortcutSaving}
                onClick={() => setShortcutCustomizerOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                loading={shortcutSaving}
                onClick={() => void saveShortcutPreference()}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        }
      >
        <p className="home-c__shortcut-intro">
          Choisissez les modules et interfaces à afficher sur l’accueil pour{" "}
          {companyName}. Seuls les accès autorisés par vos permissions sont
          proposés.
        </p>

        <label className="home-c__shortcut-search">
          <SearchOutlined aria-hidden="true" />
          <input
            type="search"
            value={shortcutSearchQuery}
            placeholder="Rechercher une interface…"
            aria-label="Rechercher une interface à ajouter"
            onChange={(event) => setShortcutSearchQuery(event.target.value)}
          />
        </label>

        <div
          className="home-c__shortcut-options"
          role="group"
          aria-label="Raccourcis à afficher"
        >
          {filteredShortcutGroups.length > 0 ? (
            filteredShortcutGroups.map((group) => (
              <section key={group.key} className="home-c__shortcut-group">
                <div className="home-c__shortcut-group-head">
                  <h3>{group.title}</h3>
                  <span>{group.items.length}</span>
                </div>
                <div className="home-c__shortcut-option-grid">
                  {group.items.map((item) => {
                    const isSelected = shortcutDraftKeys.includes(item.key);
                    const stateLabel = isSelected
                      ? "Affiché sur l’accueil"
                      : "Masqué";

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`home-c__shortcut-option ${
                          isSelected ? "home-c__shortcut-option--selected" : ""
                        }`}
                        aria-pressed={isSelected}
                        disabled={shortcutSaving}
                        onClick={() => toggleShortcutDraft(item.key)}
                      >
                        <span
                          className="home-c__shortcut-option-icon"
                          style={{
                            background: `color-mix(in srgb, ${item.color} 12%, white)`,
                            color: item.color,
                          }}
                          aria-hidden="true"
                        >
                          {item.icon}
                        </span>
                        <span className="home-c__shortcut-option-copy">
                          <strong>{item.title}</strong>
                          <small>
                            {[item.contextLabel, stateLabel]
                              .filter(Boolean)
                              .join(" · ")}
                          </small>
                        </span>
                        <span
                          className="home-c__shortcut-option-check"
                          aria-hidden="true"
                        >
                          {isSelected && <CheckOutlined />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))
          ) : (
            <div className="home-c__shortcut-no-results">
              Aucune interface ne correspond à cette recherche.
            </div>
          )}
        </div>
      </Modal>

      <CommercialOnboardingAssistant
        open={commercialOnboardingOpen}
        user={user}
        workspaceReady={workspaceReady}
        selectedCompany={selectedCompany}
        selectedWarehouse={selectedWarehouse}
        onClose={() => setCommercialOnboardingOpen(false)}
        onNavigate={(path) => navigate(path)}
        onProfileSaved={refreshUserStatus}
      />

      <Modal
        open={agendaModalOpen}
        title={
          isDailyRecap ? (
            <DailyRecapModalTitle
              data={selectedAgendaItem?.data || {}}
              createdAt={selectedAgendaItem?.created_at}
              companyNameFallback={companyName}
            />
          ) : (
            selectedAgendaItem?.title || "Détail de l’agenda"
          )
        }
        width={isDailyRecap ? 1040 : 680}
        className={`home-c__agenda-modal${
          isDailyRecap ? " home-c__agenda-modal--recap" : ""
        }`}
        onCancel={() => setAgendaModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setAgendaModalOpen(false)}>
            Fermer
          </Button>,
          selectedAgendaItem && (
            <Button
              key="snooze"
              icon={<ClockCircleOutlined />}
              loading={agendaUpdatingId === selectedAgendaItem.id}
              onClick={snoozeSelectedAgendaItem}
            >
              Reporter à demain
            </Button>
          ),
          selectedAgendaItem && (
            <Button
              key="done"
              icon={<CheckOutlined />}
              loading={agendaUpdatingId === selectedAgendaItem.id}
              onClick={() => updateAgendaState("done")}
            >
              {selectedAgendaItem.kind === "info" ? "Retirer de l’agenda" : "Terminer"}
            </Button>
          ),
          selectedAgendaItem?.action && (
            <Button
              key="business-action"
              type="primary"
              disabled={agendaUpdatingId === selectedAgendaItem.id}
              onClick={() => {
                const actionUrl = selectedAgendaItem.action.url;
                setAgendaModalOpen(false);
                if (actionUrl.startsWith("/")) navigate(actionUrl);
              }}
            >
              {selectedAgendaItem.action.label}
            </Button>
          ),
        ]}
      >
        {selectedAgendaItem && (
          <div className="home-c__agenda-detail">
            {!isDailyRecap && (
              <div className="home-c__agenda-detail-meta">
                <span className={`home-c__tone home-c__tone--${selectedAgendaItem.tone}`} />
                <span>{selectedAgendaItem.category_label}</span>
                <span>·</span>
                <span>{selectedAgendaItem.time_label}</span>
              </div>
            )}

            {isDailyRecap ? (
              <DailyRecapDetail
                data={selectedAgendaItem.data || {}}
                message={selectedAgendaItem.message}
                createdAt={selectedAgendaItem.created_at}
                companyNameFallback={companyName}
              />
            ) : (
              <>
                <p className="home-c__agenda-message">{selectedAgendaItem.message}</p>

                {agendaDetailRows.length > 0 && (
                  <dl className="home-c__agenda-details-list">
                    {agendaDetailRows.map((row) => (
                      <div key={row.key}>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </>
            )}

            <div className="home-c__agenda-action-note">
              {isDailyRecap
                ? "Ce bilan est informatif. Utilisez « Retirer de l’agenda » après consultation, ou ouvrez le détail de la clôture pour approfondir les résultats."
                : "Ouvrir la page associée ne termine pas automatiquement cette priorité. Utilisez « Terminer » lorsque le traitement est réellement effectué."}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const HomePage = () => {
  const { user, hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const isMobile = useIsMobile();
  const [pilotageRejectedCompanyId, setPilotageRejectedCompanyId] = useState(null);
  const selectedCompanyId = Number(selectedCompany);
  const pilotageRejectedForSelection =
    pilotageRejectedCompanyId !== null &&
    Number(pilotageRejectedCompanyId) === selectedCompanyId;
  const canViewRestaurantPilotage =
    isMobile &&
    !pilotageRejectedForSelection &&
    selectedCompanyHasProfile(user, selectedCompany, "restaurant") &&
    hasPermission?.("Gestion Commerciale.Dashboard.view");

  const handleProfileIneligible = React.useCallback(() => {
    setPilotageRejectedCompanyId(selectedCompanyId || null);
  }, [selectedCompanyId]);

  return canViewRestaurantPilotage ? (
    <RestaurantOwnerCockpit onProfileIneligible={handleProfileIneligible} />
  ) : (
    <OperationalHomePage />
  );
};

export default HomePage;
